/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef, useMemo, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { DataType } from '@grapecity/wijmo';
import { Box, Button, Grid } from '@mui/material';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { MdLine } from 'models/md/MdLine';
import {
  getMdInvestLineList,
  saveMdInvestLineReflectionAmt,
  getMdIvestMdlCostSummaryData,
} from 'apis/md/MdInvestResult';
import { MdIvestMdlCostSummary } from 'models/md/MdInvestResult';
import { useMessageBar } from 'hooks/useMessageBar';
import { formatWonToEok } from 'utils/FormatUtil';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';

import { ChartData } from 'chart.js';
import HorizontalBarChart from 'components/chart/HorizontalBarChart';

/**
 * @desc 투자비 산출 등록/조회 > 투자비 산출 등록 > 투자비 산출 결과 분석 탭 > 투자비 요약
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.14   jdseong       신규 생성
 */
interface Props {
  elmPrjId?: string;
  ivesMdlId?: string;
  isReadOnly?: boolean;
}

const InvestmentCostSummary = (
  { elmPrjId, ivesMdlId, isReadOnly = false, ...props }: Props,
  ref
) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [rowData, setRowData] = useState<MdLine[]>([]);
  const [costSummaryData, setCostSummaryData] = useState<MdIvestMdlCostSummary[]>([]);

  useImperativeHandle(ref, () => ({
    search: (elmPrjId, ivesMdlId) => {
      handleSearch(elmPrjId, ivesMdlId);
    },
    save: (showMsg = true) => {
      return new Promise((resolve) => {
        handleSave(showMsg).then((res) => {
          resolve(res);
        });
      });
    },
  }));

  useEffect(() => {
    handleSearch(elmPrjId, ivesMdlId);
  }, [elmPrjId, ivesMdlId]);

  // 합계 계산
  const totalAmount = useMemo(() => {
    if (!costSummaryData || costSummaryData.length === 0) {
      return 0;
    }
    return costSummaryData.reduce((sum, item) => {
      const amount = parseFloat(item?.ivesAmt ?? '0');
      return sum + amount;
    }, 0);
  }, [costSummaryData]);

  const chartData = useMemo(() => {
    if (!costSummaryData || costSummaryData.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: '투자비(억원)',
            data: [],
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
          },
        ],
      };
    }

    return {
      labels: costSummaryData.map((item) => item.categoryNm),
      datasets: [
        {
          label: `투자비(${totalAmount.toFixed(1)}억원)`,
          data: costSummaryData.map(
            (item) =>
              //formatWonToEok(item?.ivesAmt, { fractionDigits: 1, emptyValue: 0 })
              item?.ivesAmt
          ),
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
        },
      ],
    };
  }, [costSummaryData, totalAmount]);

  const handleSearch = (elmPrjId, ivesMdlId) => {
    if (!!elmPrjId && !!ivesMdlId) {
      getMdInvestLineList(elmPrjId, ivesMdlId).then((result) => setRowData(result));
    }
    getMdIvestMdlCostSummaryData(elmPrjId, ivesMdlId ?? '').then((result) =>
      setCostSummaryData(result)
    );
  };

  const handleSave = (showMsg = true) => {
    return new Promise((resolve) => {
      const rows = (rowData || []).filter((o) => !!o.crudKey);
      if (rows.length) {
        const totAmt = CommonUtil.convertNumber(rowData[0]?.totalAmt);
        const sumAmt = (rowData || []).reduce(
          (acc, cur) => acc + CommonUtil.convertNumber(cur.ivesRflcAmt),
          0
        );
        if (totAmt != sumAmt) {
          resolve({
            successOrNot: 'N',
            data: t(
              `md.label.변경한 라인당 투자비(억원)의 합이 전체 합과 맞지 않습니다. (입력값 : ${sumAmt} 합: ${totAmt})`,
              `변경한 라인당 투자비(억원)의 합이 전체 합과 맞지 않습니다. (입력값 : ${sumAmt}, 합 : ${totAmt})`
            ),
          });
        }
        saveMdInvestLineReflectionAmt(rows).then((res) => {
          resolve(res);
        });
      } else {
        // 저장된게 없으므로 패스
        resolve({
          successOrNot: 'Y',
        });
      }
    });
  };

  const updateRowHeight = (grid) => {
    if (!grid) return;
    const rowCount = grid.rows.length;
    const minHeight = 25;
    const maxHeight = 37;
    const maxRow = 8; // 최대 8줄까지는 스크롤 없이 노출
    const minRow = 5;

    let ratio;
    if (rowCount >= maxRow) {
      ratio = 1; // 8줄 이상: ratio = 1 (최소 높이 25px)
    } else if (rowCount <= minRow) {
      ratio = 0; // 5줄 이하: ratio = 0 (최대 높이 37px)
    } else {
      // 6~7줄: 비율에 따라 동적 조절
      ratio = (rowCount - minRow) / (maxRow - minRow);
    }
    const rowHeight = maxHeight - ratio * (maxHeight - minHeight);
    // 행 높이 설정
    grid.rows.defaultSize = rowHeight;
    // padding과 line-height도 비례해서 조절
    const padding = Math.round(3 + (1 - ratio) * 3);
    const lineHeight = Math.round(19 + (1 - ratio) * 6);
    grid.hostElement.style.setProperty('--row-height', `${rowHeight}px`);
    grid.hostElement.style.setProperty('--row-padding', `${padding}px`);
    grid.hostElement.style.setProperty('--row-line-height', `${lineHeight}px`);
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'prdnLnTpCdNm',
        header: String(t('com.label.공정', '공정')),
        width: 65,
        minWidth: 30,
        align: 'center',
        cssClass: 'text-center transposed-header',
        isReadOnly: true,
        allowMerging: true,
      },
      {
        binding: 'prdnLnNm',
        header: String(t('com.label.호기', '호기')),
        width: 55,
        minWidth: 30,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'ivesRflcAmt',
        header: String(t('md.label.투자비(억원)', '투자비(억원)')),
        width: '*',
        minWidth: 50,
        align: 'center',
        dataType: DataType.String,
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'crudKey',
        visible: false,
      },
    ];
  }, [isReadOnly]);

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const newVal = grid.activeEditor?.value;
    // 투자비
    if ('ivesRflcAmt' === binding) {
      if (newVal) {
        if (!/^[0-9.]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
        const value = parseFloat(newVal);
        if (!_.isNaN(value)) {
          setTimeout(() => {
            grid.setCellData(e.row, e.col, CommonUtil.roundNumber(value, 1));
          }, 0);
        }
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.rows.defaultSize = 25;
    grid.hostElement.classList.add('cost-grid');
    grid.loadedRows.addHandler(() => {
      updateRowHeight(grid);
    });
  };

  useEffect(() => {
    if (gridRef.current) {
      setTimeout(() => {
        updateRowHeight(gridRef.current);
      }, 100);
    }
  }, [rowData]);

  return (
    <>
      <style>
        {`
          .cost-grid .wj-cell:not(.transposed-header)[role="gridcell"] {
              min-height: var(--row-height, 25px) !important;
              height: var(--row-height, 25px) !important;
              padding: var(--row-padding, 3px) !important;
              line-height: var(--row-line-height, 19px) !important;
          }
        `}
      </style>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.투자비 요약', '투자비 요약')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="refresh pdf-ignore excel-ignore"
            onClick={() => handleSearch(elmPrjId, ivesMdlId)}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <HorizontalBarChart
            data={chartData}
            unit={'억원'}
            barThickness={14}
            showXGrid={true}
            showYGrid={true}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomGrid
            layoutDefinition={layoutDefinition}
            rowData={rowData}
            height={205}
            isSelector={false}
            isFilter={false}
            isReadOnly={isReadOnly}
            allowPinning={false}
            initialized={onInitialized}
            cellEditEnding={onCellEditEnding}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default React.forwardRef(InvestmentCostSummary);
